<template>
	<Modal id="wishlistModal" v-if="show" @hide="$emit('update:show', !show)" max-width="max-w-lg">
		<div v-if="!showItemAlreadyInWishlist" class="p-4 text-ihh-midnight-gray w-2xl">
			<div v-if="store?.user?.id">
				<span class="flex justify-center text-lg font-semibold mb-4">Add to Wishlist</span>

				<div v-if="hasWishlists" class="mb-4">
					<label for="wishlistPick" class="mb-1 block text-sm font-medium">Choose a list</label>
					<select id="wishlistPick" v-model="selectedWishlist" class="block w-full p-2 border border-gray-300 rounded-md">
						<option disabled value="">Select a wishlist</option>
						<option v-for="wishlist in listOptions" :key="wishlist.id" :value="wishlist.id">{{ wishlist.name }}</option>
					</select>
					<button
						type="button"
						:disabled="!selectedWishlist || saving"
						@click="addToWishlist"
						class="mt-3 w-full bg-ihh-dark-salmon font-ihh-bold text-white p-2 rounded-md hover:bg-ihh-salmon disabled:opacity-50"
					>
						Add
					</button>
				</div>

				<div v-if="hasWishlists" class="my-4 flex items-center gap-3 text-xs text-[#6E7288]">
					<span class="h-px flex-1 bg-[#E7E2F0]"></span>
					or
					<span class="h-px flex-1 bg-[#E7E2F0]"></span>
				</div>

				<div>
					<label for="newWishlistName" class="mb-1 block text-sm font-medium">Create new list</label>
					<p v-if="!hasWishlists" class="mb-2 text-sm text-[#6E7288]">Name a list and this product will be added to it.</p>
					<input
						id="newWishlistName"
						v-model="newWishlist.name"
						type="text"
						class="block w-full p-2 border border-gray-300 rounded-md"
						placeholder="List name"
						@keydown.enter.prevent="createWishlist"
					/>
					<label class="mt-3 flex items-center text-sm">
						<input type="checkbox" v-model="newWishlist.isPublic" />
						<span class="ml-2">Make list shareable?</span>
					</label>
					<button
						type="button"
						@click="createWishlist"
						:disabled="!isValidName || saving"
						class="mt-3 w-full bg-ihh-dark-salmon font-ihh-bold text-white p-2 rounded-md hover:bg-ihh-salmon disabled:opacity-50"
					>
						Create and add
					</button>
				</div>
			</div>
			<div class="flex flex-col items-center justify-center h-64 text-center p-4" v-else>
				<p class="text-lg font-semibold text-gray-700">Please log in to manage your wishlists.</p>
				<router-link to="/login" class="bg-ihh-dark-salmon text-ihh-white rounded-lg px-14 py-2 mt-4 mb-2">Login</router-link>
				<button type="button" @click="$emit('update:show', false)" class="text-xs underline underline-offset-2 text-ihh-gray-500">Return to products</button>
			</div>
		</div>
		<div v-else class="w-full flex flex-col items-center justify-center h-64 text-center p-4">
			<div class="w-full flex items-center justify-center">
				<img src="/images/ob-heart-0129.svg" class="w-12 h-12" alt="" />
				<div class="text-3xl text-ihh-dark-salmon">Good News!</div>
			</div>
			<p class="text-base my-4 font-semibold text-ihh-midnight-gray">
				This item is already in your wishlist. Click OK to choose a different list or Exit to return to products.
			</p>
			<div class="flex gap-2">
				<button type="button" @click="handleOK" class="text-base text-white bg-ihh-dark-salmon rounded-lg px-14 py-2 mt-4 mb-2">OK</button>
				<button type="button" @click="handleExit" class="text-base text-ihh-midnight-gray border-ihh-dark-salmon border-2 rounded-lg px-14 py-2 mt-4 mb-2">Exit</button>
			</div>
		</div>
	</Modal>
</template>

<script>
import Swal from 'sweetalert2'
import Mango from '../../helpers/mango.js'
import { captureWishlistError } from '../../helpers/sentry'
import { handleWishlistFailure } from '../../helpers/wishlistAuth.js'

function wishlistWarningText(warnings) {
	if (Array.isArray(warnings)) return warnings.filter(Boolean).join(' ')
	return warnings || ''
}

function mutationFailed(response) {
	if (!response || response.authorized === false || response.valid === false) return true
	if (wishlistWarningText(response.warnings)) return true
	const id = response.response?.id || response.id
	return !id
}

export default {
	name: 'WishlistModal',
	props: {
		show: {
			type: Boolean,
			default: false,
		},
		product: {
			type: Object,
			required: true,
		},
	},
	emits: ['update:show'],
	inject: ['store'],
	data() {
		return {
			selectedWishlist: '',
			newWishlist: {
				name: '',
				isPublic: false,
				bcCustomerId: this.store?.user?.bcId || null,
				items: [],
			},
			saving: false,
			showItemAlreadyInWishlist: false,
			syncToken: 0,
		}
	},
	methods: {
		blankWishlist() {
			return {
				name: '',
				isPublic: false,
				bcCustomerId: this.store?.user?.bcId || null,
				items: [],
			}
		},
		noteWishlistError(error, details, alertText) {
			const signedOut = handleWishlistFailure(error, {
				store: this.store,
				capture: captureWishlistError,
				...details,
			})
			if (signedOut || !alertText) return signedOut
			Swal.fire({ text: alertText, icon: 'error' })
			return false
		},
		sameProduct(item) {
			const itemBc = Number(item?.productBcId)
			const productBc = Number(this.product?.bcId)
			if (Number.isFinite(itemBc) && itemBc > 0 && itemBc === productBc) return true
			const product = item?.product
			const productId = Array.isArray(product) ? product[0]?.id || product[0] : product?.id || product
			return !!(productId && this.product?.id && String(productId) === String(this.product.id))
		},
		async createWishlist() {
			const name = this.newWishlist.name?.trim()
			if (!name || this.saving) return
			this.saving = true
			let createdId = null
			try {
				const response = await Mango.wishlists.save(
					{
						name,
						isPublic: !!this.newWishlist.isPublic,
						bcCustomerId: this.store?.user?.bcId || null,
						items: [],
					},
					false,
					true,
				)
				if (mutationFailed(response)) {
					const warning = wishlistWarningText(response?.warnings) || 'Could not create your wishlist. Please try again.'
					captureWishlistError(warning, {
						action: 'createWishlist',
						warnings: response?.warnings,
						store: this.store,
						extra: { listName: name },
					})
					Swal.fire({ text: warning, icon: 'error' })
					return
				}

				const created = { items: [], ...response.response }
				const existing = Array.isArray(this.store.wishlists) ? this.store.wishlists : []
				this.store.wishlists = [...existing, created]
				this.syncToken++
				this.selectedWishlist = created.id
				createdId = created.id
				this.newWishlist = this.blankWishlist()
			} catch (error) {
				this.noteWishlistError(error, { action: 'createWishlist', extra: { listName: name } }, 'Could not create your wishlist. Please try again.')
			} finally {
				this.saving = false
			}
			if (createdId) await this.addToWishlist()
		},
		async addToWishlist() {
			if (this.saving || !this.selectedWishlist || !this.product?.id) return
			if (this.itemAlreadyOnList) {
				this.showItemAlreadyInWishlist = true
				return
			}

			this.saving = true
			try {
				const productBcId = Number(this.product.bcId)
				const response = await Mango.wishlists.save(
					{
						id: this.selectedWishlist,
						$push: {
							items: {
								name: this.product.name,
								product: [this.product.id],
								productBcId: Number.isFinite(productBcId) && productBcId > 0 ? productBcId : this.product.bcId,
							},
						},
					},
					false,
					true,
				)
				if (mutationFailed(response)) {
					captureWishlistError('Could not add this item to your list.', {
						action: 'addToWishlist',
						warnings: response?.warnings,
						store: this.store,
						extra: { wishlistId: this.selectedWishlist, productId: this.product?.id },
					})
					Swal.fire({ text: 'Could not add this item to your list. Please try again.', icon: 'error' })
					return
				}

				await this.syncWishlists()
				Swal.fire({
					icon: 'success',
					title: 'Added to Wishlist',
					showConfirmButton: false,
					timer: 2000,
					toast: true,
					position: 'center',
				})
				this.$emit('update:show', false)
				this.selectedWishlist = ''
			} catch (error) {
				this.noteWishlistError(
					error,
					{ action: 'addToWishlist', extra: { wishlistId: this.selectedWishlist, productId: this.product?.id } },
					'Could not add this item to your list. Please try again.',
				)
			} finally {
				this.saving = false
			}
		},
		async syncWishlists() {
			if (!this.store?.user?.id) return
			const token = ++this.syncToken
			try {
				const response = await Mango.wishlists({ search: { author: this.store.user.id }, limit: 100 })
				if (token !== this.syncToken || !Array.isArray(response)) return
				this.store.wishlists = response
			} catch (error) {
				if (token !== this.syncToken) return
				this.noteWishlistError(error, { action: 'syncWishlists' })
			}
		},
		handleExit() {
			this.showItemAlreadyInWishlist = false
			this.selectedWishlist = ''
			this.$emit('update:show', false)
		},
		handleOK() {
			this.showItemAlreadyInWishlist = false
			this.selectedWishlist = ''
		},
	},
	computed: {
		listOptions() {
			return Array.isArray(this.store.wishlists) ? this.store.wishlists : []
		},
		hasWishlists() {
			return this.listOptions.length > 0
		},
		isValidName() {
			return this.newWishlist?.name?.trim().length > 0
		},
		itemAlreadyOnList() {
			const current = this.listOptions.find((list) => list.id === this.selectedWishlist)
			if (!current?.items?.length) return false
			return current.items.some((item) => this.sameProduct(item))
		},
	},
	watch: {
		show: {
			immediate: true,
			handler(open) {
				if (!open) return
				this.selectedWishlist = ''
				this.showItemAlreadyInWishlist = false
				this.newWishlist = this.blankWishlist()
				this.syncWishlists()
			},
		},
	},
}
</script>
